"use client";

import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";
import { buttonVariants, type ButtonVariants } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FloatingWindowContextValue {
  /** null = no coordinates yet; the window renders centered on screen. */
  position: { x: number; y: number } | null;
  setPosition: (pos: { x: number; y: number }) => void;
}

const FloatingWindowContext =
  React.createContext<FloatingWindowContextValue | null>(null);

function useFloatingWindow() {
  const context = React.useContext(FloatingWindowContext);
  if (!context) {
    throw new Error(
      "FloatingWindow components must be used within a FloatingWindow",
    );
  }
  return context;
}

interface FloatingWindowProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Initial coordinates. Omit to open centered on screen. */
  defaultPosition?: { x: number; y: number };
  /** When true, renders as a modal (focus-trapped). When false (default), a
   *  non-modal floating window the user can interact around. */
  modal?: boolean;
}

function FloatingWindow({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  defaultPosition,
  modal = false,
}: FloatingWindowProps) {
  const [position, setPosition] = React.useState<{
    x: number;
    y: number;
  } | null>(defaultPosition ?? null);

  const contextValue = React.useMemo(
    () => ({ position, setPosition }),
    [position],
  );

  return (
    <FloatingWindowContext.Provider value={contextValue}>
      <DialogPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        modal={modal}
        onOpenChange={(next, details) => {
          // A floating window stays put when the user clicks/interacts
          // outside it — only the close button / Escape dismiss it.
          if (!next && details.reason === "outside-press") return;
          onOpenChange?.(next);
        }}
      >
        {children}
      </DialogPrimitive.Root>
    </FloatingWindowContext.Provider>
  );
}

type FloatingWindowTriggerProps = DialogPrimitive.Trigger.Props;

function FloatingWindowTrigger({
  children,
  ...props
}: FloatingWindowTriggerProps) {
  return (
    <DialogPrimitive.Trigger data-slot="floating-window-trigger" {...props}>
      {children}
    </DialogPrimitive.Trigger>
  );
}

interface FloatingWindowCloseProps
  extends DialogPrimitive.Close.Props,
    ButtonVariants {}

function FloatingWindowClose({
  className,
  size = "icon-sm",
  variant = "ghost",
  ...props
}: FloatingWindowCloseProps) {
  return (
    <DialogPrimitive.Close
      data-slot="floating-window-close"
      className={cn(
        buttonVariants({ variant, size }),
        "text-muted-foreground hover:text-foreground transition-colors",
        className,
      )}
      {...props}
    >
      <Icon name="IconCrossLarge" size={16} />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  );
}

type FloatingWindowContainerProps = DialogPrimitive.Popup.Props;

function FloatingWindowContainer({
  children,
  className,
  ...props
}: FloatingWindowContainerProps) {
  const { position } = useFloatingWindow();

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Popup
        data-slot="floating-window-container"
        className={cn(
          "border-border/50 bg-surface-elevated fixed z-[9999] flex flex-col overflow-hidden rounded-xl border shadow-2xl shadow-black/20",
          "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
          "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          !position && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
          className,
        )}
        style={position ? { left: position.x, top: position.y } : undefined}
        onWheel={(e) => e.stopPropagation()}
        {...props}
      >
        {children}
      </DialogPrimitive.Popup>
    </DialogPrimitive.Portal>
  );
}

interface FloatingWindowHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  showClose?: boolean;
  disableDrag?: boolean;
}

function FloatingWindowHeader({
  className,
  children,
  showClose = true,
  disableDrag = false,
  ...props
}: FloatingWindowHeaderProps) {
  const { position, setPosition } = useFloatingWindow();
  const isDragging = React.useRef(false);
  const dragOffset = React.useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disableDrag) return;
    if ((e.target as HTMLElement).closest("button")) return;

    // A centered window has no coordinates yet — pin its current rect so the
    // first drag starts from where it sits.
    let origin = position;
    if (!origin) {
      const rect = e.currentTarget
        .closest('[data-slot="floating-window-container"]')
        ?.getBoundingClientRect();
      origin = { x: rect?.left ?? 0, y: rect?.top ?? 0 };
      setPosition(origin);
    }

    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - origin.x,
      y: e.clientY - origin.y,
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;

      const clampedX = Math.max(0, Math.min(newX, window.innerWidth - 100));
      const clampedY = Math.max(0, Math.min(newY, window.innerHeight - 50));

      setPosition({ x: clampedX, y: clampedY });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div
      data-slot="floating-window-header"
      className={cn(
        "border-border/50 bg-surface-header flex shrink-0 justify-between gap-2 border-b px-4 py-3 select-none",
        !disableDrag && "cursor-grab active:cursor-grabbing",
        className,
      )}
      onMouseDown={handleMouseDown}
      {...props}
    >
      <Icon name="IconDotGrid3x3" size={12} className="self-center shrink-0" />
      <div className="flex flex-1 flex-col justify-center gap-0.5">
        {children}
      </div>
      <div className="flex items-center gap-1">
        {showClose && <FloatingWindowClose />}
      </div>
    </div>
  );
}

function FloatingWindowTitle({
  className,
  children,
  ...props
}: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="floating-window-title"
      className={cn(
        "text-foreground inline-flex items-center gap-1 text-sm font-medium [&_svg]:size-4",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}

function FloatingWindowDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="floating-window-description"
      className={cn("text-muted-foreground text-xs", className)}
      {...props}
    />
  );
}

interface FloatingWindowBreadcrumbsProps
  extends React.HTMLAttributes<HTMLDivElement> {
  path: string[];
  separator?: React.ReactNode;
}

function FloatingWindowBreadcrumbs({
  className,
  path,
  separator,
  ...props
}: FloatingWindowBreadcrumbsProps) {
  return (
    <div
      data-slot="floating-window-breadcrumbs"
      className={cn(
        "text-muted-foreground flex items-center gap-1 text-xs",
        className,
      )}
      {...props}
    >
      {path.map((item, index) => (
        <React.Fragment key={index}>
          <span className={index === 0 ? "text-foreground/70" : ""}>
            {item}
          </span>
          {index !== path.length - 1 &&
            (separator ?? <Icon name="IconChevronRight" size={12} />)}
        </React.Fragment>
      ))}
    </div>
  );
}

type FloatingWindowContentProps = React.HTMLAttributes<HTMLDivElement>;

function FloatingWindowContent({
  className,
  ...props
}: FloatingWindowContentProps) {
  return (
    <div
      data-slot="floating-window-content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 outline-none",
        className,
      )}
      {...props}
    />
  );
}

type FloatingWindowFooterProps = React.HTMLAttributes<HTMLDivElement>;

function FloatingWindowFooter({
  className,
  ...props
}: FloatingWindowFooterProps) {
  return (
    <div
      data-slot="floating-window-footer"
      className={cn(
        "border-border/50 bg-surface-secondary mt-auto flex shrink-0 flex-row gap-3 border-t p-4",
        className,
      )}
      {...props}
    />
  );
}

function FloatingWindowAccordion(
  props: React.ComponentProps<typeof Accordion>,
) {
  return <Accordion {...props} />;
}

function FloatingWindowAccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionItem>) {
  return (
    <AccordionItem
      className={cn(
        "bg-foreground/2 border border-b-0 first:rounded-t-sm last:rounded-b-sm last:border-b",
        className,
      )}
      {...props}
    />
  );
}

function FloatingWindowAccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionTrigger>) {
  return (
    <AccordionHeader className="flex">
      <AccordionTrigger
        className={cn(
          "focus-visible:border-ring group focus-visible:ring-ring/50 flex flex-1 cursor-pointer items-center justify-between gap-4 rounded-md p-4 text-left text-sm font-medium transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50",
          className,
        )}
        {...props}
      >
        <div className="flex flex-1 items-center gap-1.5">{children}</div>
        <Icon
          name="IconChevronDownMedium"
          size={20}
          className="text-foreground/50 shrink-0 transition-all duration-200 group-data-[panel-open]:rotate-180"
        />
      </AccordionTrigger>
    </AccordionHeader>
  );
}

function FloatingWindowAccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionContent>) {
  return (
    <AccordionContent {...props}>
      <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </AccordionContent>
  );
}

export {
  FloatingWindow,
  FloatingWindowAccordion,
  FloatingWindowAccordionContent,
  FloatingWindowAccordionItem,
  FloatingWindowAccordionTrigger,
  FloatingWindowBreadcrumbs,
  FloatingWindowClose,
  FloatingWindowContainer,
  FloatingWindowContent,
  FloatingWindowDescription,
  FloatingWindowFooter,
  FloatingWindowHeader,
  FloatingWindowTitle,
  FloatingWindowTrigger,
  useFloatingWindow,
};

export type {
  FloatingWindowBreadcrumbsProps,
  FloatingWindowContainerProps,
  FloatingWindowContentProps,
  FloatingWindowFooterProps,
  FloatingWindowHeaderProps,
  FloatingWindowProps,
  FloatingWindowTriggerProps,
};
